import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Field,
  Input,
  PageHeader,
  Select,
  Toolbar,
  formatMoney,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { MasterTable, Messages, useAction } from './document.js';
import { apiSend, useResource, type AdminWriteResult } from '../lib/api.js';
// The page rules, the letterhead and the print frame, shared with every other
// printed document. `esc` below stays local: it takes a nullable.
import { PAGE, letterhead, send } from '../lib/print.js';
import { company } from '../lib/reference-data.js';

/**
 * The party register — customers and suppliers, and the chart rows they own.
 *
 * Since `0030_party_sub_ledger.sql` a customer *is* a level-5 account under
 * `AR_TRADE` and a supplier one under `AP_LOCAL` or `AP_FOREIGN`. This is where
 * those accounts are opened: saving a party mints the account, derives its code
 * and points the party at it, and the Chart of Accounts then displays what this
 * produced. The controls are named by key rather than by code throughout this
 * file, including on screen — `0037` renumbered the chart out from under the
 * codes that used to be written here, and a form quoting a code the book has not
 * used for four migrations is worse than one quoting none.
 *
 * **Un-ticking a role closes that account while it is still empty.** Not a
 * cosmetic tidy: the register used to leave it behind, so a party corrected from
 * supplier to customer the same afternoon ended up owning one account on each
 * side of the chart. `releasePartyLedgerAccount` refuses once anything has
 * posted, which is the same rule `deleteAccount` keeps.
 *
 * **The direction is one-way and that is the design.** The chart's maintenance
 * screen validates a code somebody typed against a tree an accountant drew; a
 * supplier has fifteen fields of legal identity that an account does not have
 * and should not grow. So the register owns the party, the API owns the account,
 * and there is no field here that re-points a party at an arbitrary account —
 * "who is this supplier" and "where does the tree put them" have different right
 * answers, and only the first is a person's to give.
 *
 * A party may be both, and ticking both opens two accounts. That is not an edge
 * case being tolerated: a supplier who also buys from us has a receivable and a
 * payable, they may not be netted, and `accounts_party_uq` is
 * `(company, party, parent)` precisely so both can exist.
 */

interface PartyRow {
  id: string;
  code: string;
  name: string;
  legalName: string | null;
  isCustomer: boolean;
  isSupplier: boolean;
  origin: string;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  taxId: string | null;
  isFiler: boolean;
  isSalesTaxRegistered: boolean;
  contactPerson: string | null;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  website: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string | null;
  currency: string;
  paymentTermsDays: number | null;
  creditLimit: string | null;
  isActive: boolean;
  receivableAccount: string | null;
  receivableAccountName: string | null;
  payableAccount: string | null;
  payableAccountName: string | null;
  ledgerLines: number;
  salesCount: number;
  purchaseCount: number;
}

interface CompanyLetterhead {
  name: string;
  legalName: string | null;
  ntn: string | null;
  strn: string | null;
  cuin: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  province: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  baseCurrency: string;
  reportFooter: string | null;
}

interface PartyRefs {
  parties: PartyRow[];
  company: CompanyLetterhead | null;
  currencies: Array<{ code: string; name: string }>;
  /**
   * The control accounts a role opens the party's own account under, by system
   * key — `AR_TRADE`, `AP_LOCAL`, `AP_FOREIGN`. Read rather than written down,
   * because the codes moved under 0037 and the form went on quoting the old ones.
   */
  controls: Record<string, { code: string; name: string } | undefined>;
  origins: string[];
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

type Tab = 'SUPPLIER' | 'CUSTOMER';

/**
 * A control account as `Accounts Payable — Local 2-1-1-1`, or a shrug.
 *
 * Name then code, because the name is what somebody is checking and the code is
 * how they find it in the chart. The fallback says the chart is missing the
 * account rather than inventing a code for it — a book whose `AP_FOREIGN` has
 * never been pointed anywhere has a real problem, and a plausible-looking number
 * here would hide it until the first foreign purchase failed to post.
 */
function controlLabel(
  controls: Record<string, { code: string; name: string } | undefined> | undefined,
  key: string,
): string {
  const account = controls?.[key];
  return account ? `${account.name} ${account.code}` : 'no account mapped';
}

type Draft = Omit<
  PartyRow,
  | 'id'
  | 'receivableAccount'
  | 'receivableAccountName'
  | 'payableAccount'
  | 'payableAccountName'
  | 'ledgerLines'
  | 'salesCount'
  | 'purchaseCount'
  | 'paymentTermsDays'
  | 'creditLimit'
  | 'countryCode'
> & {
  mode: 'create' | 'edit';
  /**
   * The two commercial terms, as text because they are typed.
   *
   * They have been columns since `0007` and enforced by `check_credit_limit`
   * since `0015`; this register read them back and had no field for either, so
   * every party sat at the default limit of 0 — which that function reads as "no
   * limit set" and lets through. The control existed and the number it enforced
   * against could not be entered.
   */
  creditLimit: string;
  paymentTermsDays: string;
};

function blank(tab: Tab): Draft {
  return {
    mode: 'create',
    code: '',
    name: '',
    legalName: '',
    isCustomer: tab === 'CUSTOMER',
    isSupplier: tab === 'SUPPLIER',
    origin: 'LOCAL',
    ntn: '',
    strn: '',
    cuin: '',
    taxId: '',
    isFiler: true,
    isSalesTaxRegistered: false,
    contactPerson: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    postalCode: '',
    currency: 'PKR',
    creditLimit: '',
    paymentTermsDays: '',
    isActive: true,
  };
}

/**
 * The legal identity block, in the order it is read out loud.
 *
 * The same twelve facts the company profile keeps about us, because a purchase
 * document has two sides and both of them are a legal entity. `cuin` is the one
 * column `0031` had to add; the other eleven have been on `parties` since `0007`
 * and were simply never editable, there being no screen that wrote a party.
 */
const IDENTITY: Array<[keyof Draft, string, string?]> = [
  ['name', 'Company name'],
  ['legalName', 'Legal name', 'As registered, if it differs from the trading name'],
  ['ntn', 'NTN'],
  ['strn', 'STRN'],
  ['cuin', 'CUIN', 'Incorporation number — blank for a sole proprietor or an AOP'],
  ['taxId', 'Foreign tax ID', 'For a party registered outside Pakistan'],
  ['addressLine1', 'Address'],
  ['addressLine2', 'Address line 2'],
  ['city', 'City'],
  ['province', 'Province'],
  ['postalCode', 'Postal code'],
  ['contactPerson', 'Contact person'],
  ['phone', 'Phone'],
  ['mobile', 'Mobile'],
  ['email', 'Email'],
  ['website', 'Website'],
];

export function PartiesScreen() {
  const [tab, setTab] = useState<Tab>('SUPPLIER');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [search, setSearch] = useState('');
  const res = useResource<PartyRefs>('/masters/parties', {
    ...auth(),
    role: tab,
    include: includeInactive ? 'inactive' : undefined,
  });
  const { busy, notice, problem, run } = useAction(res.reload);
  const [draft, setDraft] = useState<Draft | null>(null);

  const parties = useMemo(() => {
    const all = res.data?.parties ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((p) =>
      [p.code, p.name, p.legalName, p.ntn, p.city, p.receivableAccount, p.payableAccount]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [res.data, search]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const save = () =>
    void run(async () => {
      if (!draft) throw new Error('No draft');
      const { mode, code, ...rest } = draft;
      const result =
        mode === 'create'
          ? await apiSend<AdminWriteResult>('POST', '/masters/parties', { ...rest, code }, auth())
          : await apiSend<AdminWriteResult>(
              'PATCH',
              `/masters/parties/${encodeURIComponent(code)}`,
              rest,
              auth(),
            );
      setDraft(null);
      return result;
    });

  const supplierTab = tab === 'SUPPLIER';

  return (
    <>
      <PageHeader
        breadcrumb={`Sales & Purchase / ${supplierTab ? 'Suppliers' : 'Customers'}`}
        title={supplierTab ? 'Suppliers' : 'Customers'}
        description={
          supplierTab
            ? 'Everyone you buy from, and what you owe each of them.'
            : 'Everyone you sell to, and what each of them owes you.'
        }
      />

      <Messages notice={notice} problem={problem} />

      {/*
        The tab strip and the search box belong to the register, so they stand
        down with it while a party is being written. Switching from Suppliers to
        Customers half way through typing one is not an action anybody means,
        and a search box over a form searches nothing that is on screen.
      */}
      {!draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardBody className="flex flex-wrap items-center gap-2.5">
            <Toolbar>
              <Button
                size="sm"
                variant={supplierTab ? 'primary' : 'ghost'}
                onClick={() => (setTab('SUPPLIER'), setDraft(null))}
              >
                Suppliers
              </Button>
              <Button
                size="sm"
                variant={!supplierTab ? 'primary' : 'ghost'}
                onClick={() => (setTab('CUSTOMER'), setDraft(null))}
              >
                Customers
              </Button>
            </Toolbar>
            <div className="min-w-[220px] flex-1">
              <Input
                value={search}
                placeholder="Code, name, NTN, city or account"
                aria-label="Search parties"
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card flush className="mb-3.5" data-noprint="1">
          <CardHeader
            title={
              draft.mode === 'create'
                ? `New ${supplierTab ? 'supplier' : 'customer'}`
                : `Edit ${draft.code}`
            }
            subtitle="What is filled in here is what prints on the party sheet and heads their side of a purchase document."
            actions={
              <Toolbar>
                <Button size="sm" onClick={() => setDraft(null)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={!draft.code || !draft.name || (!draft.isCustomer && !draft.isSupplier)}
                  onClick={save}
                >
                  Save
                </Button>
              </Toolbar>
            }
          />
          <CardBody className="flex flex-col gap-3.5">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field label="Code" required hint="SUP-0004, CUS-0007 — fixed once documents exist">
                <Input
                  value={draft.code}
                  disabled={draft.mode === 'edit'}
                  onChange={(e) => set('code', e.target.value.toUpperCase())}
                />
              </Field>
              <Field
                label="Origin"
                hint={`Decides which payable a supplier settles against — ${controlLabel(res.data?.controls, 'AP_FOREIGN')} for a foreign one`}
              >
                <Select value={draft.origin} onChange={(e) => set('origin', e.target.value)}>
                  {(res.data?.origins ?? ['LOCAL', 'FOREIGN']).map((o) => (
                    <option key={o} value={o}>
                      {o[0]}
                      {o.slice(1).toLowerCase()}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Base currency" hint="What they invoice and settle in">
                <Select value={draft.currency} onChange={(e) => set('currency', e.target.value)}>
                  {(res.data?.currencies ?? []).map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {/*
                Each role names the account it actually opens, read out of the
                chart. Which payable follows the Origin box beside it, the same
                way `ensurePartyLedgerAccount` chooses it — a foreign supplier
                settles against the FCY payable because the two revalue
                differently — so changing Origin re-labels this in place.

                Un-ticking a role closes that account, and the hint says so:
                the register will do it while nothing has posted there and refuse
                once something has, which is the one thing worth knowing before
                clicking rather than after.
              */}
              <Field
                label="Roles"
                hint="Both is legal — and opens two accounts. Un-ticking one closes its account while it is still empty."
              >
                <div className="flex flex-col gap-1.5 pt-1">
                  <Checkbox
                    checked={draft.isSupplier}
                    onChange={(e) => set('isSupplier', e.target.checked)}
                    label={`Supplier — payable under ${controlLabel(
                      res.data?.controls,
                      draft.origin === 'FOREIGN' ? 'AP_FOREIGN' : 'AP_LOCAL',
                    )}`}
                  />
                  <Checkbox
                    checked={draft.isCustomer}
                    onChange={(e) => set('isCustomer', e.target.checked)}
                    label={`Customer — receivable under ${controlLabel(res.data?.controls, 'AR_TRADE')}`}
                  />
                </div>
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {IDENTITY.map(([key, label, hint]) => (
                <Field key={String(key)} label={label} hint={hint} required={key === 'name'}>
                  <Input
                    value={String(draft[key] ?? '')}
                    onChange={(e) => set(key, e.target.value as Draft[typeof key])}
                  />
                </Field>
              ))}
            </div>

            {/*
              Commercial terms.

              The credit limit is the number `check_credit_limit` compares the
              customer's **ledger** balance against — `party_outstanding_base`,
              which is the sum of their receivable lines on posted vouchers, not
              a figure any screen keeps — and a sales invoice that would carry
              them past it is refused unless a manager records a reason on the
              document. So this field is the control itself, and it is stated in
              the hint rather than left to be discovered at Post.

              Blank means no limit. That is the column default and what the
              function has always read `0` as; it is offered as a blank box
              rather than a `0` because "no limit" and "a limit of nothing" are
              different sentences and only one of them is true.
            */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field
                label={`Credit limit${draft.isCustomer ? '' : ' (customers)'}`}
                hint="Blank is no limit. A sale that would take their ledger balance past this is refused."
              >
                <Input
                  inputMode="decimal"
                  placeholder="No limit"
                  value={draft.creditLimit}
                  onChange={(e) => set('creditLimit', e.target.value)}
                />
              </Field>
              <Field label="Payment terms" hint="Days from the invoice date — it sets the due date">
                <Input
                  inputMode="numeric"
                  placeholder="0 — due on invoice"
                  value={draft.paymentTermsDays}
                  onChange={(e) => set('paymentTermsDays', e.target.value)}
                />
              </Field>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Field label="Tax status">
                <div className="flex flex-col gap-1.5 pt-1">
                  <Checkbox
                    checked={draft.isFiler}
                    onChange={(e) => set('isFiler', e.target.checked)}
                    label="On the active taxpayer list — drives the withholding rate"
                  />
                  <Checkbox
                    checked={draft.isSalesTaxRegistered}
                    onChange={(e) => set('isSalesTaxRegistered', e.target.checked)}
                    label="Sales tax registered"
                  />
                </div>
              </Field>
              {draft.mode === 'edit' && (
                <Field label="Active">
                  <Checkbox
                    checked={draft.isActive}
                    onChange={(e) => set('isActive', e.target.checked)}
                    label="Offered on documents"
                  />
                </Field>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {!draft && (
        <MasterTable
          title={supplierTab ? 'Suppliers' : 'Customers'}
          subtitle="A party who has posted cannot be deleted — the balance in their account is real. Switch them off instead."
          loading={res.loading}
          error={res.error}
          empty={supplierTab ? 'No suppliers' : 'No customers'}
          onNew={res.data?.canCreate ? () => setDraft(blank(tab)) : undefined}
          newLabel={supplierTab ? 'New supplier' : 'New customer'}
          toggle={{
            on: includeInactive,
            onToggle: () => setIncludeInactive((v) => !v),
            label: 'Show inactive',
          }}
          columns={['Code', 'Name', 'Chart account', 'NTN', 'City', 'Documents', '']}
        >
          {parties.map((p) => (
            <tr key={p.id} className="bb-row-h border-b border-[var(--line-2)]">
              <td className="bb-mono px-[var(--pad)] text-[12.5px] font-medium">{p.code}</td>
              <td className="px-2.5 text-[13px]">
                {p.name}
                {p.origin === 'FOREIGN' && (
                  <Badge tone="info" className="ml-2">
                    Foreign
                  </Badge>
                )}
                {p.isCustomer && p.isSupplier && (
                  <Badge tone="warn" className="ml-2">
                    Both
                  </Badge>
                )}
                {!p.isActive && (
                  <Badge tone="mute" className="ml-2">
                    Inactive
                  </Badge>
                )}
              </td>
              <td className="bb-mono px-2.5 text-[11.5px] text-[var(--ink-2)]">
                {[supplierTab ? p.payableAccount : p.receivableAccount, supplierTab ? null : null]
                  .filter(Boolean)
                  .join('') || <span className="text-[var(--danger)]">no account</span>}
              </td>
              <td className="bb-mono px-2.5 text-right text-[11.5px]">{p.ntn ?? '—'}</td>
              <td className="px-2.5 text-right text-[12.5px]">{p.city ?? '—'}</td>
              <td className="bb-num px-2.5 text-right text-[12.5px]">
                {p.salesCount + p.purchaseCount || '—'}
              </td>
              <td className="px-[var(--pad)] text-right" data-noprint="1">
                <Toolbar>
                  <Button size="sm" onClick={() => printPartySheet(p)}>
                    Print
                  </Button>
                  <Button
                    size="sm"
                    disabled={!res.data?.canEdit}
                    onClick={() =>
                      setDraft({
                        mode: 'edit',
                        code: p.code,
                        name: p.name,
                        legalName: p.legalName ?? '',
                        isCustomer: p.isCustomer,
                        isSupplier: p.isSupplier,
                        origin: p.origin,
                        ntn: p.ntn ?? '',
                        strn: p.strn ?? '',
                        cuin: p.cuin ?? '',
                        taxId: p.taxId ?? '',
                        isFiler: p.isFiler,
                        isSalesTaxRegistered: p.isSalesTaxRegistered,
                        contactPerson: p.contactPerson ?? '',
                        phone: p.phone ?? '',
                        mobile: p.mobile ?? '',
                        email: p.email ?? '',
                        website: p.website ?? '',
                        addressLine1: p.addressLine1 ?? '',
                        addressLine2: p.addressLine2 ?? '',
                        city: p.city ?? '',
                        province: p.province ?? '',
                        postalCode: p.postalCode ?? '',
                        currency: p.currency,
                        // `0` is what the column holds when nobody has set a
                        // limit, and it is what `check_credit_limit` reads as
                        // "no limit". Showing it as an empty box says the same
                        // thing without looking like a limit of nothing.
                        creditLimit:
                          p.creditLimit && Number(p.creditLimit) > 0 ? p.creditLimit : '',
                        paymentTermsDays:
                          p.paymentTermsDays === null || p.paymentTermsDays === 0
                            ? ''
                            : String(p.paymentTermsDays),
                        isActive: p.isActive,
                      })
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={busy}
                    disabled={
                      !res.data?.canDelete ||
                      p.ledgerLines > 0 ||
                      p.salesCount > 0 ||
                      p.purchaseCount > 0
                    }
                    onClick={() =>
                      void run(() =>
                        apiSend<AdminWriteResult>(
                          'DELETE',
                          `/masters/parties/${encodeURIComponent(p.code)}`,
                          undefined,
                          auth(),
                        ),
                      )
                    }
                  >
                    Delete
                  </Button>
                </Toolbar>
              </td>
            </tr>
          ))}
        </MasterTable>
      )}
    </>
  );
}

// ── The party sheet, on paper ────────────────────────────────────────────────

/** Text into HTML. The fields below are all typed by a person. */
function esc(value: string | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function lines(...parts: Array<string | null | undefined>): string {
  return parts.filter(Boolean).map(esc).join(', ');
}

/**
 * One party's legal record, printed.
 *
 * Both sides of it: our letterhead and theirs, because the sheet is filed as
 * evidence of who we are dealing with and a page carrying only their half does
 * not say who was asking. It is composed as its own document and printed from a
 * hidden iframe, the same way every other printed document is — an iframe rather
 * than `window.open` because a popup blocker eats the second one silently.
 *
 * **Our half comes from `letterhead()`, not from `/party-refs`.** This function
 * used to take a `CompanyLetterhead` off that payload and lay it out itself, with
 * `@page { margin: 16mm }` — so Chrome stamped its own date and URL over the top
 * and bottom of a sheet filed as evidence — and with `co.ntn` / `co.strn` read
 * straight off the record, which no payload of that shape can filter because the
 * three **Print on documents** flags are not on it. Same two defects the voucher
 * print had, from the same copied original.
 *
 * The chart account is on the sheet deliberately. It is the fact that ties this
 * record to the ledger, and somebody reconciling a supplier statement against
 * `2-1-1-1-0002` should not have to go and look it up.
 */
function printPartySheet(p: PartyRow) {
  const co = company();
  const field = (label: string, value: string | null | undefined) =>
    value ? `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>` : '';

  const account = p.isSupplier ? p.payableAccount : p.receivableAccount;
  const accountName = p.isSupplier ? p.payableAccountName : p.receivableAccountName;
  const both =
    p.isCustomer && p.isSupplier
      ? `<tr><th>Also</th><td>${esc(
          p.isSupplier
            ? `Customer · ${p.receivableAccount ?? '—'}`
            : `Supplier · ${p.payableAccount ?? '—'}`,
        )}</td></tr>`
      : '';

  const doc = `<!doctype html>
<html><head><meta charset="utf-8"><title>${esc(p.code)} — ${esc(p.name)}</title>
<style>
  ${PAGE}
  * { box-sizing: border-box; }
  body { font: 12px/1.45 "IBM Plex Sans", system-ui, sans-serif; color: #000; margin: 0;
         background: #fff; }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
          gap: 24px; border-bottom: 1.5px solid #000; padding-bottom: 8px; }
  .co { font-size: 16px; font-weight: 600; }
  .muted { color: #444; font-size: 11px; }
  h1 { font-size: 14px; margin: 16px 0 4px; text-transform: uppercase; letter-spacing: .08em; }
  .sub { font-size: 11px; color: #444; padding-bottom: 10px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  th, td { border-bottom: 1px solid #bbb; padding: 5px 6px; vertical-align: top;
           text-align: left; font-size: 11.5px; }
  th { width: 32%; font-weight: 600; color: #333; }
  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: .06em;
       margin: 14px 0 4px; border-bottom: 1.2px solid #000; padding-bottom: 3px; }
  .mono { font-variant-numeric: tabular-nums; }
  .sigs { display: flex; gap: 60px; padding-top: 48px; }
  .sig { flex: 1; border-top: 1px solid #000; padding-top: 4px; font-size: 11px; }
  .foot { padding-top: 18px; font-size: 10px; color: #444; }
</style></head>
<body>
  ${letterhead()}

  <h1>${esc(p.isSupplier ? 'Supplier record' : 'Customer record')}</h1>
  <div class="sub">${esc(p.code)} · ${esc(p.name)}${
    co?.baseCurrency ? ` · books kept in ${esc(co.baseCurrency)}` : ''
  }</div>

  <h2>Legal identity</h2>
  <table>
    ${field('Company name', p.name)}
    ${field('Legal name', p.legalName)}
    ${field('NTN', p.ntn)}
    ${field('STRN', p.strn)}
    ${field('CUIN', p.cuin)}
    ${field('Foreign tax ID', p.taxId)}
    ${field('Filer status', p.isFiler ? 'On the active taxpayer list' : 'Non-filer')}
    ${field('Sales tax', p.isSalesTaxRegistered ? 'Registered' : 'Not registered')}
  </table>

  <h2>Address and contact</h2>
  <table>
    ${field('Address', lines(p.addressLine1, p.addressLine2))}
    ${field('City', p.city)}
    ${field('Province', p.province)}
    ${field('Postal code', p.postalCode)}
    ${field('Country', p.countryCode)}
    ${field('Contact person', p.contactPerson)}
    ${field('Phone', p.phone)}
    ${field('Mobile', p.mobile)}
    ${field('Email', p.email)}
    ${field('Website', p.website)}
  </table>

  <h2>Ledger</h2>
  <table>
    ${field('Base currency', p.currency)}
    ${field('Origin', p.origin === 'FOREIGN' ? 'Foreign' : 'Local')}
    ${account ? `<tr><th>Chart account</th><td class="mono">${esc(account)}${accountName ? ` · ${esc(accountName)}` : ''}</td></tr>` : ''}
    ${both}
    ${field('Payment terms', p.paymentTermsDays === null ? null : `${p.paymentTermsDays} days from invoice`)}
    ${field('Credit limit', p.creditLimit && Number(p.creditLimit) > 0 ? formatMoney(p.creditLimit, { decimals: 0 }) : 'None set')}
    ${field('Documents on file', String(p.salesCount + p.purchaseCount))}
  </table>

  <div class="sigs">
    <div class="sig">&nbsp;<div class="muted">Verified by</div></div>
    <div class="sig">&nbsp;<div class="muted">Authorised signature</div></div>
  </div>

  <div class="foot">
    ${esc(co?.reportFooter ?? '')}
    Printed ${esc(new Date().toLocaleString())}.
  </div>
</body></html>`;

  send(doc);
}
